import { Box, Typography, useTheme, styled } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Button } from 'components'
import { Icon } from 'packages/eid-icons'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { CustomDialog } from '../../../../components/Popups/EditAssignments'
import TimeConstraintComp from '../../../../components/AddTimeConstraint/TimeConstraint'
import PeopleSearch from 'components/Filters/PeopleSearch'
import AccessAssignmentAccessLevels from './AccessAssignmentAccessLevels'
import { useAddItemsToCart, useSaveAccessAssignmentToFolders } from 'hooks'
import cartHelpers from 'containers/Cart/cartHelpers'

export const CloseIconeBox = styled(Box)({
    '& svg': {
        position: 'absolute',
        top: '-10px',
        right: '-15px',
        cursor: 'pointer',
    },
})

const useStyles = makeStyles((theme) => ({
    AddAccessModalMain: {
        '& .MuiDialog-paper': {
            maxWidth: '440px',
        },
    },
    NewAccessAssignmentModal: {
        '& h2': {
            fontSize: '18px',
            color: '#1b1f1f',
            fontWeight: '600',
        },
    },
    ConstraintModalHeader: {
        padding: '32px',
    },
    AssignmentModalHeader: {
        padding: '20px 32px',
    },
    AssignmentModalHeading: {
        padding: '16px 32px',
        borderTop: '1px solid #0000002e !important',
        borderBottom: '1px solid #0000002e !important',
        backgroundImage:
            'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%) !important',
        backgroundSize: '8.00px 8.00px !important',
    },
    AssignmentModalInner: {
        padding: '20px 32px',
    },
    AssignmentModalFooter: {
        padding: '24px 32px 10px 32px',
        boxShadow: '0 3px 7px 4px rgba(0, 0, 0, 0.08)',
        '& button': {
            border: `solid 1px ${theme?.palette?.primary?.main}`,
            padding: '9px 24px',
        },
    },
    btnSave: {
        background: theme?.palette?.primary?.main,
        color: '#fff',
        maxWidth: '240px',
        '& svg': {
            marginRight: '15px',
        },
        '&:hover': {
            backgroundColor: theme?.palette?.primary?.main,
        },
    },
    btnCancel: {
        color: theme?.palette?.primary?.main,
        marginLeft: '30px',
        maxWidth: '90px',
    },
    AssignmentTimeInner: {
        padding: '20px 32px',
        paddingTop: '0',
    },
    errorMessage: {
        color: 'red',
        fontSize: '12px',
    },
}))

const AddAcessAssignentsFolder = ({ sharedFolder, refetch }) => {
    const [selectedPerson, setSelectedPerson] = useState(null)
    const [selectedAccessLevel, setSelectedAccessLevel] = useState(null)
    const [errorMessage, setErrorMessage] = useState('')
    const [openAcessAssignmentFolderModal, setOpenAcessAssignmentFolderModal] =
        useState(false)
    const { t } = useTranslation()
    const theme = useTheme()
    const classes = useStyles({})
    const currentDatetime = moment.utc().second(0)
    const [isTimeContrained, setIsTimeConstrained] = useState(false)
    const [addItemsToCart, { isLoading }] = useAddItemsToCart()
    const [{ selectedStartDate, selectedEndDate }, setSelectedDate] = useState({
        selectedStartDate: currentDatetime,
        selectedEndDate: null,
    })

    const handleStartDate = (startDateToSet) => {
        setSelectedDate((prev) => {
            return { ...prev, selectedStartDate: startDateToSet }
        })
        if (errorMessage) {
            setErrorMessage('')
        }
    }

    const handleEndDate = (endDateToSet) => {
        setSelectedDate((prev) => {
            return { ...prev, selectedEndDate: endDateToSet }
        })
        if (errorMessage) {
            setErrorMessage('')
        }
    }

    const handleSave = () => {
        if (!selectedPerson && !selectedAccessLevel) {
            setErrorMessage(t('SharedFolder_PersonAndAccessLevelIsRequired'))
        } else if (!selectedPerson) {
            setErrorMessage(t('SharedFolder_PersonIsRequired'))
        } else if (!selectedAccessLevel) {
            setErrorMessage(t('SharedFolder_AccessLevelIsRequired'))
        } else if (isTimeContrained && !selectedEndDate) {
            setErrorMessage(t('SharedFolder_EndDateIsRequired'))
        } else if (
            isTimeContrained &&
            moment(selectedStartDate).isAfter(moment(selectedEndDate))
        ) {
            setErrorMessage(t('SharedFolder_InvalidDate'))
        } else {
            const cartItem = cartHelpers.addRemoveAccessAssignmentToFolder({
                assignmentType: 'Add',
                resource: sharedFolder,
                accessAssignment: {
                    ...selectedPerson,
                    assigneeId: selectedPerson.id,
                    resourceFriendlyName: selectedPerson.friendlyName,
                    resourceTypeFriendlyName: selectedPerson.resourceTypeName,
                    resourceTypeRoleId: selectedAccessLevel.id,
                },
                validFrom: isTimeContrained ? selectedStartDate : null,
                validTo: isTimeContrained ? selectedEndDate : null,
            })

            addItemsToCart([cartItem]).then(() => {
                refetch()
                setOpenAcessAssignmentFolderModal(false)
            })
        }
    }

    return (
        <>
            <Button
                onClick={() => setOpenAcessAssignmentFolderModal(true)}
                style={{
                    borderRadius: '4px',
                    padding: '0px 20px',
                    backgroundColor: theme?.palette?.primary?.main,
                    border: 'solid 1px #01ae8f',
                    color: '#ffffff',
                    fontSize: '14px',
                    '&:hover': {
                        backgroundColor: theme?.palette?.primary?.main,
                    },
                }}
                startIcon={
                    <Icon
                        name={'Plus'}
                        fill="white"
                        style={{
                            width: '1.6rem',
                            height: '1.6rem',
                        }}
                    />
                }
            >
                {t('AddNewRbacAssignment')}
            </Button>
            <CustomDialog
                open={openAcessAssignmentFolderModal}
                onClose={() => setOpenAcessAssignmentFolderModal(false)}
                title={t('')}
                className={classes.AddAccessModalMain}
            >
                <Box className={classes.NewAccessAssignmentModal}>
                    <Box className={classes.AssignmentModalHeader}>
                        <CloseIconeBox
                            onClick={() =>
                                setOpenAcessAssignmentFolderModal(false)
                            }
                        >
                            <Icon
                                name="Close"
                                width="16px"
                                height="16px"
                                color="#959598"
                            />
                        </CloseIconeBox>
                        <Typography variant="h2">
                            {t('AddNewRbacAssignment')}
                        </Typography>
                    </Box>
                    <Box>
                        <Box className={classes.AssignmentModalHeading}>
                            <Typography variant="h2">
                                {t('SelectToWhomYouWishToGrantAccess')}
                            </Typography>
                        </Box>
                        <Box className={classes.AssignmentModalInner}>
                            <Box>
                                <PeopleSearch
                                    labelKey="PersonNameToSelect"
                                    value={selectedPerson}
                                    onChange={(_, value) => {
                                        if (errorMessage) {
                                            setErrorMessage('')
                                        }
                                        setSelectedPerson(value)
                                    }}
                                />
                            </Box>
                            <Box marginTop="24px">
                                <AccessAssignmentAccessLevels
                                    labelKey="AccessLevel"
                                    url={`/api/resourceassignments/resourcetyperoles/${sharedFolder?.id}`}
                                    value={selectedAccessLevel}
                                    onChange={(_, value) => {
                                        if (errorMessage) {
                                            setErrorMessage('')
                                        }
                                        setSelectedAccessLevel(value)
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box>
                        <Box className={classes.AssignmentModalHeading}>
                            <Typography variant="h2">
                                {t('TimeConstraint')}
                            </Typography>
                        </Box>
                        <TimeConstraintComp
                            handleStartDate={handleStartDate}
                            handleEndDate={handleEndDate}
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            hasInvalidDates={true}
                            validationMessage={''}
                            handleTimeConstraint={(value) =>
                                setIsTimeConstrained(value)
                            }
                            timeConstrained={isTimeContrained}
                        />
                    </Box>
                    <Box className={classes.AssignmentModalFooter}>
                        <Button
                            onClick={() => handleSave()}
                            loading={isLoading}
                            className={classes.btnSave}
                        >
                            <Fragment>
                                <Icon
                                    name="Save"
                                    width="16px"
                                    height="16px"
                                    color="#fff"
                                />
                                {t('Save')}
                            </Fragment>
                        </Button>
                        <Button
                            className={classes.btnCancel}
                            onClick={() =>
                                setOpenAcessAssignmentFolderModal(false)
                            }
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                    <Box style={{ padding: '0px 32px', marginBottom: '10px' }}>
                        <Typography className={classes.errorMessage}>
                            {errorMessage}
                        </Typography>
                    </Box>
                </Box>
            </CustomDialog>
        </>
    )
}

export default AddAcessAssignentsFolder
